/**
 * Haptic feedback utility for mobile devices
 * Uses the Vibration API when available
 */

type HapticIntensity = 'light' | 'medium' | 'heavy';

const vibrationPatterns: Record<HapticIntensity, number | number[]> = {
  light: 10,
  medium: 25,
  heavy: 50,
};

/**
 * Check if haptic feedback is supported
 */
export const isHapticSupported = (): boolean => {
  return 'vibrate' in navigator;
};

/**
 * Trigger haptic feedback
 * @param intensity - The intensity of the haptic feedback
 */
export const triggerHaptic = (intensity: HapticIntensity = 'light'): void => {
  if (!isHapticSupported()) return;
  
  try {
    const pattern = vibrationPatterns[intensity];
    navigator.vibrate(pattern);
  } catch (error) {
    // Silently fail if vibration is not allowed
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Trigger a success haptic pattern
 */
export const triggerSuccessHaptic = (): void => {
  if (!isHapticSupported()) return;
  
  try {
    // Double tap pattern for success
    navigator.vibrate([15, 50, 15]);
  } catch (error) {
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Trigger an error haptic pattern
 */
export const triggerErrorHaptic = (): void => {
  if (!isHapticSupported()) return;
  
  try {
    // Long buzz for error
    navigator.vibrate([100, 30, 100]);
  } catch (error) {
    console.debug('Haptic feedback not available:', error);
  }
};
